import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthLayout } from '@/features/authentication/layouts/AuthLayout';
import { LoginPage } from '@/features/authentication/pages/LoginPage';
import { ForceChangePasswordPage } from '@/features/authentication/pages/ForceChangePasswordPage';
import { AppLayout, HomePage } from '@/features/app-shell';
import { UserManagementPage, CreateUserPage, EditUserPage } from '@/features/users';
import { AuthorizationManagementPage, CreateRolePage, EditRolePage } from '@/features/authorization';
import { PicklistsPage, MaterialRequestsPage, MaterialRequestDetailPage, PicklistCreationPage, PicklistDetailPage, PicklistAddItemsPage, PicklistGuidePage } from '@/features/material-requests';
import { MroDashboardPage } from '@/features/mro-dashboard/pages';
import { SystemHealthPage } from '@/features/system-health/pages';
import { DocumentCapturePage, DocumentViewerPage, PublicDocumentPage } from '@/features/document-capture';
import { ReceivingPage, PurchaseOrderSelectPage, ReceivingFormPage, ReceivingDetailPage } from '@/features/receiving';
import { PurchaseOrderListPage } from '@/features/purchase-orders/pages/PurchaseOrderListPage';
import { PurchaseOrderDetailPage } from '@/features/purchase-orders/pages/PurchaseOrderDetailPage';
import { ProfilePage } from '@/features/profile';
import { NotificationsPage } from '@/features/notifications/pages/NotificationsPage';
import { SystemModulesPage } from '@/features/system-modules';
import { ReleaseNotesPage, ReleaseNotesAdminPage } from '@/features/release-notes';
import { DownloadsPage } from '@/features/downloads';
import { AnnouncementsAdminPage } from '@/features/announcements';
import { ActivityPage } from '@/features/activity';
import { ComposeRedirect, QuotationsListPage, QuotationWorkspacePage } from '@/features/quotations';
import {
  SalesOrderComposePage,
  SalesOrderListPage,
  SalesOrderWorkspacePage,
} from '@/features/sales-orders';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicRoute } from './PublicRoute';
import { AuthenticatedRoute } from './AuthenticatedRoute';
import { PermissionRoute } from './PermissionRoute';
import { ModuleGuard } from './ModuleGuard';

export function AppRouter() {
  return (
    <Routes>
      {/* Public customer document link (86d3udn5u, T8) — BARE top-level route, deliberately OUTSIDE
          PublicRoute (which bounces authenticated users to /) and ProtectedRoute (which demands
          login). Possession of the capability token is the only authorization; staff and customers
          alike open it directly. */}
      <Route path="/p/:token" element={<PublicDocumentPage />} />

      {/* Public routes - redirect to home if already authenticated */}
      <Route element={<PublicRoute />}>
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>
      </Route>

      {/* Force change password - authenticated but uses AuthLayout (not AppLayout) */}
      <Route element={<AuthenticatedRoute />}>
        <Route element={<AuthLayout />}>
          <Route path="/change-password" element={<ForceChangePasswordPage />} />
        </Route>
      </Route>

      {/* Protected routes - redirect to login if not authenticated */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/profile" element={<ProfilePage />} />

          {/* N7 (86d3w2kf4) — the full notification inbox behind the popover's "See all".
              Deliberately ungated: neither a ModuleGuard nor a PermissionRoute. Every authenticated
              user has an inbox, and it only ever shows rows addressed to them (the repository filters
              on RecipientUserId), so a permission gate here would lock people out of their OWN
              notifications. */}
          <Route path="/notifications" element={<NotificationsPage />} />

          {/* Administration — User Management (USEM module) */}
          <Route element={<ModuleGuard moduleCode="USEM" />}>
            <Route element={<PermissionRoute permission={PERMISSIONS.USER_VIEW_USER_LIST} />}>
              <Route path="/administration/users" element={<UserManagementPage />} />
              <Route path="/administration/users/:id" element={<EditUserPage />} />
            </Route>
            <Route element={<PermissionRoute permission={PERMISSIONS.USER_CREATE_USER} />}>
              <Route path="/administration/users/new" element={<CreateUserPage />} />
            </Route>
          </Route>

          {/* Administration — Authorization (AUTM module) */}
          <Route element={<ModuleGuard moduleCode="AUTM" />}>
            <Route element={<PermissionRoute anyOf={[PERMISSIONS.AUTH_VIEW_ROLE_LIST, PERMISSIONS.AUTH_VIEW_USER_ROLE_LIST]} />}>
              <Route path="/administration/authorization" element={<AuthorizationManagementPage />} />
              <Route path="/administration/authorization/roles/:id" element={<EditRolePage />} />
            </Route>
            <Route element={<PermissionRoute permission={PERMISSIONS.AUTH_CREATE_ROLE} />}>
              <Route path="/administration/authorization/roles/new" element={<CreateRolePage />} />
            </Route>
          </Route>

          {/* Administration — Module Management (MODM — never blocked) */}
          <Route element={<PermissionRoute permission={PERMISSIONS.MOD_VIEW_MODULE_LIST} />}>
            <Route path="/administration/modules" element={<SystemModulesPage />} />
          </Route>

          {/* Administration — Release Notes */}
          <Route element={<PermissionRoute permission={PERMISSIONS.RELEASE_MANAGE_RELEASES} />}>
            <Route path="/administration/release-notes" element={<ReleaseNotesAdminPage />} />
          </Route>

          {/* Administration — Announcements (ANNT module) */}
          <Route element={<ModuleGuard moduleCode="ANNT" />}>
            <Route element={<PermissionRoute permission={PERMISSIONS.ANNOUNCEMENT_VIEW} />}>
              <Route path="/administration/announcements" element={<AnnouncementsAdminPage />} />
            </Route>
          </Route>

          {/* Administration — Activity feed (ACTV module) */}
          <Route element={<ModuleGuard moduleCode="ACTV" />}>
            <Route element={<PermissionRoute permission={PERMISSIONS.ACTIVITY_VIEW_ALL} />}>
              <Route path="/activity" element={<ActivityPage />} />
            </Route>
          </Route>

          {/* Administration — System Health telemetry dashboard (86d3peur0) */}
          <Route element={<PermissionRoute permission={PERMISSIONS.SYSTEM_VIEW_HEALTH} />}>
            <Route path="/system-health" element={<SystemHealthPage />} />
          </Route>

          {/* Services — Document Capture (86d3rrfbf, gated 86d3y0cku).
              The old exemption ("permissions only, no ModuleGuard") existed because DSCN shipped
              IsEnabled = false and a gate would have blanked the page for everyone. That reason expired
              the day the module went live: the Module Management toggle became decorative — an operator
              could switch Document Capture off and every user still walked straight in, which is what
              was reported from production. Reachability is now the module's call, not the router's.
              NOTE: the DSCN row is still IsEnabled = false as of this change; the owner flips it on
              after deploy. Until then this guard is doing exactly what it says on the tin.
              A disabled module blocks SYS_ADMIN too (ModuleEnforcementMiddleware + isModuleAccessible
              agree on this) — that is intended, not an oversight to "fix" later. */}
          <Route element={<ModuleGuard moduleCode="DSCN" />}>
            <Route
              element={
                <PermissionRoute
                  anyOf={[
                    PERMISSIONS.DOCSCAN_VIEW_DR,
                    PERMISSIONS.DOCSCAN_VIEW_INV,
                    PERMISSIONS.DOCSCAN_VIEW_INT,
                    PERMISSIONS.DOCSCAN_VIEW_CM,
                    PERMISSIONS.DOCSCAN_VIEW_OP,
                    PERMISSIONS.DOCSCAN_VIEW_DV,
                    PERMISSIONS.DOCSCAN_VIEW_JV,
                    // ⚠️ This list is DUPLICATED from DOCSCAN_VIEW_PERMISSIONS in
                    // features/app-shell/lib/menu.ts — it is not imported from it. Adding a family to
                    // only one side is silent: menu.ts alone makes the nav item appear and this route
                    // still bounces the user to /. Change both together, or import one from the other.
                    // Upload-only station account (86d3rrfee, must-fix #2): the folder watcher lives on
                    // this page, so an upload-only login must reach it — without this it bounces to / and
                    // the station is dead on arrival. It sees an empty (server-scoped) registry + its card.
                    PERMISSIONS.DOCSCAN_UPLOAD,
                  ]}
                />
              }
            >
              <Route path="/documents" element={<DocumentCapturePage />} />
            </Route>

            {/* Viewer — the NetSuite landing target. Authenticated but NOT permission-gated: the API
                answers a neutral 404 for anything the user can't see, so the page itself is the access
                boundary and unauthorized deep-links land on the neutral not-found state. Inside the
                module gate all the same: every byte it renders comes from /api/document-capture, so
                without the gate a disabled module leaves this page fetching against a 503 and showing
                its neutral "not found" — telling the user the document does not exist when in fact the
                module is off. The gate's notice is the honest answer. */}
            <Route path="/d/:docNo" element={<DocumentViewerPage />} />
          </Route>

          {/* What's New — public changelog (any authenticated user) */}
          <Route path="/release-notes" element={<ReleaseNotesPage />} />

          {/* Downloads — any authenticated user */}
          <Route path="/downloads" element={<DownloadsPage />} />

          {/* DOP Roadmap — moved into Home tab. Redirect old bookmarks. */}
          <Route path="/roadmap" element={<Navigate to="/?tab=roadmap" replace />} />

          {/* Quotations (QUOT module) */}
          <Route element={<ModuleGuard moduleCode="QUOT" />}>
            <Route
              element={
                <PermissionRoute
                  anyOf={[
                    PERMISSIONS.QUOTATIONS_VIEW_QUOTATION,
                    PERMISSIONS.QUOTATIONS_VIEW_ALL_QUOTATIONS,
                  ]}
                />
              }
            >
              <Route path="/quotations" element={<QuotationsListPage />} />
              {/* 86d3k9589: the master-detail workspace is now the sole edit/post surface.
                  The legacy compose page was retired; its route redirects to the workspace
                  (preserving old bookmarks) via ComposeRedirect. */}
              <Route path="/quotations/workspace" element={<QuotationWorkspacePage />} />
              <Route path="/quotations/compose/:draftId" element={<ComposeRedirect />} />
              {/* Legacy redirect — old bookmarks to the history page land on the list. */}
              <Route path="/quotations/history" element={<Navigate to="/quotations" replace />} />
            </Route>
          </Route>

          {/* FO-SOI-3: Sales Order Intake (SORD module). FO drawdown → NetSuite SO. */}
          <Route element={<ModuleGuard moduleCode="SORD" />}>
            <Route element={<PermissionRoute permission={PERMISSIONS.SALES_ORDERS_VIEW_SALES_ORDER} />}>
              <Route path="/sales-orders" element={<SalesOrderListPage />} />
              {/* FO-SOI-4: master-detail workspace. Coexists with the legacy
                  list and compose pages — the list page links here as an
                  opt-in CTA while we validate the new layout in real use. */}
              <Route path="/sales-orders/workspace" element={<SalesOrderWorkspacePage />} />
              <Route path="/sales-orders/compose/:draftId" element={<SalesOrderComposePage />} />
            </Route>
          </Route>

          {/* MRO — Receiving (RECV module) */}
          <Route element={<ModuleGuard moduleCode="RECV" />}>
            <Route element={<PermissionRoute anyOf={[PERMISSIONS.RECEIVING_VIEW, PERMISSIONS.RECEIVING_CREATE, PERMISSIONS.RECEIVING_POST]} />}>
              <Route path="/mro/purchase-orders" element={<PurchaseOrderListPage />} />
              <Route path="/mro/purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
              <Route path="/mro/receiving" element={<ReceivingPage />} />
              <Route path="/mro/receiving/:id" element={<ReceivingDetailPage />} />
            </Route>
            <Route element={<PermissionRoute permission={PERMISSIONS.RECEIVING_CREATE} />}>
              <Route path="/mro/receiving/new" element={<PurchaseOrderSelectPage />} />
              <Route path="/mro/receiving/new/:poId" element={<ReceivingFormPage />} />
            </Route>
          </Route>

          {/* MRO — Issuance (ISSE module) */}
          <Route element={<ModuleGuard moduleCode="ISSE" />}>
            <Route element={<PermissionRoute permission={PERMISSIONS.ISSUANCE_VIEW_DASHBOARD} />}>
              <Route path="/mro/dashboard" element={<MroDashboardPage />} />
            </Route>
            <Route element={<PermissionRoute anyOf={[PERMISSIONS.ISSUANCE_VIEW_PICKLIST, PERMISSIONS.ISSUANCE_VIEW_REQUEST_LIST]} />}>
              <Route
                path="/mro/issuance"
                element={<Navigate to="/mro/issuance/picklists" replace />}
              />
              <Route path="/mro/issuance/picklists" element={<PicklistsPage />} />
              <Route path="/mro/issuance/mrs" element={<MaterialRequestsPage />} />
              <Route path="/mro/issuance/mrs/:id" element={<MaterialRequestDetailPage />} />
              <Route path="/mro/issuance/picklist/:id" element={<PicklistDetailPage />} />
              <Route path="/mro/issuance/picklist-guide" element={<PicklistGuidePage />} />
            </Route>
            <Route element={<PermissionRoute permission={PERMISSIONS.ISSUANCE_CREATE_PICKLIST} />}>
              <Route path="/mro/issuance/create" element={<PicklistCreationPage />} />
            </Route>
            <Route element={<PermissionRoute permission={PERMISSIONS.ISSUANCE_EDIT_PICKLIST} />}>
              <Route path="/mro/issuance/picklist/:id/add-items" element={<PicklistAddItemsPage />} />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
